import React, { useState } from 'react';
import { ChatMessage } from '../../services/chatService';
import { favoritesApiService } from '../../services/favoritesApi';

interface MessageBubbleProps {
  message: ChatMessage;
  onShare?: (content: string) => void;
  onPlayAudio?: (content: string, messageId: string) => void;
  isPlayingAudio?: boolean;
  isAuthenticated?: boolean;
  onFavoriteChange?: () => void; // Callback pour notifier le parent d'un changement de favori
  isTyping?: boolean; // Indique si le message est en cours de frappe
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  onShare,
  onPlayAudio,
  isPlayingAudio = false,
  isAuthenticated = false,
  onFavoriteChange,
  isTyping = false,
}) => {
  const isUser = message.type === 'user';
  const [isFavorite, setIsFavorite] = useState(false);

  // Vérifier si le message est en favori
  React.useEffect(() => {
    const checkFavorite = async () => {
      if (!isUser && isAuthenticated) {
        try {
          const favorite = await favoritesApiService.isFavorite(message.content, 'general');
          setIsFavorite(favorite);
        } catch (error) {
          console.error('Erreur lors de la vérification du favori:', error);
        }
      }
    };
    checkFavorite();
  }, [message.content, isUser, isAuthenticated]);

  const handleAddToFavorites = async () => {
    if (isUser || !isAuthenticated) return;

    // Extraire le type et les détails du message
    let type: 'verse' | 'hadith' | 'dua' | 'prayer_times' | 'general' = 'general';
    let title = message.content.substring(0, 50);
    let arabic: string | undefined;
    let reference: string | undefined;

    if (message.content.includes('📖')) {
      type = 'verse';
      const match = message.content.match(/\*\*(.*?)\*\*/);
      if (match) title = match[1];
    } else if (message.content.includes('📜')) {
      type = 'hadith';
      const match = message.content.match(/📚\s*(.*)/);
      if (match) reference = match[1];
    } else if (message.content.includes('🤲')) {
      type = 'dua';
    } else if (message.content.includes('🕌') && message.content.includes('Horaires')) {
      type = 'prayer_times';
    }

    try {
      await favoritesApiService.addFavorite({
        type,
        title,
        content: message.content,
        arabic,
        reference,
      });
      setIsFavorite(true);
      // Notifier le parent pour recharger la liste des favoris
      if (onFavoriteChange) {
        onFavoriteChange();
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout aux favoris:', error);
    }
  };

  const handleRemoveFromFavorites = async () => {
    if (isUser || !isAuthenticated) return;
    try {
      const favorites = await favoritesApiService.getAllFavorites();
      const favorite = favorites.find((f) => f.content === message.content);
      if (favorite) {
        await favoritesApiService.removeFavorite(favorite.id);
        setIsFavorite(false);
        // Notifier le parent pour recharger la liste des favoris
        if (onFavoriteChange) {
          onFavoriteChange();
        }
      }
    } catch (error) {
      console.error('Erreur lors de la suppression des favoris:', error);
    }
  };

  const handleShare = () => {
    if (onShare) {
      onShare(message.content);
    } else {
      // Partage par défaut
      if (navigator.share) {
        navigator.share({
          title: 'Réponse de HanafiyaAI',
          text: message.content,
        }).catch(() => {
          // Fallback: copier dans le presse-papier
          navigator.clipboard.writeText(message.content);
          alert('Réponse copiée dans le presse-papier !');
        });
      } else {
        navigator.clipboard.writeText(message.content);
        alert('Réponse copiée dans le presse-papier !');
      }
    }
  };

  const handlePlayAudio = () => {
    if (onPlayAudio) {
      onPlayAudio(message.content, message.id);
    }
  };

  // Fonction pour formater le texte avec markdown simple
  const formatMessage = (text: string): React.ReactNode => {
    // Détecter et remplacer les vidéos [VIDEO:URL]
    const videoRegex = /\[VIDEO:(.+?)\]/g;
    const parts: Array<{ type: 'text' | 'video'; content: string }> = [];
    let lastIndex = 0;
    let match;

    while ((match = videoRegex.exec(text)) !== null) {
      // Ajouter le texte avant la vidéo
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: text.substring(lastIndex, match.index) });
      }
      // Ajouter la vidéo
      parts.push({ type: 'video', content: match[1] });
      lastIndex = match.index + match[0].length;
    }
    // Ajouter le texte restant
    if (lastIndex < text.length) {
      parts.push({ type: 'text', content: text.substring(lastIndex) });
    }

    // Si aucune vidéo trouvée, traiter comme avant
    if (parts.length === 0) {
      parts.push({ type: 'text', content: text });
    }

    return parts.map((part, partIndex) => {
      if (part.type === 'video') {
        return (
          <div key={partIndex} className="my-4">
            <video
              controls
              className="w-full rounded-lg shadow-lg max-w-full"
              src={part.content}
            >
              Votre navigateur ne supporte pas la lecture de vidéos.
              <a href={part.content} className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">
                Télécharger la vidéo
              </a>
            </video>
          </div>
        );
      }

      // Traiter le texte normalement
      const lines = part.content.split('\n');
      return (
        <React.Fragment key={partIndex}>
          {lines.map((line, lineIndex) => {
            // Détecter les titres (lignes avec **)
            if (line.startsWith('**') && line.endsWith('**')) {
              return (
                <h3 key={lineIndex} className="font-bold text-lg mb-2 mt-3 first:mt-0">
                  {line.replace(/\*\*/g, '')}
                </h3>
              );
            }
            // Détecter le texte en gras
            const textParts = line.split(/(\*\*.*?\*\*)/g);
            return (
              <p key={lineIndex} className="mb-2 last:mb-0">
                {textParts.map((textPart, textPartIndex) => {
                  if (textPart.startsWith('**') && textPart.endsWith('**')) {
                    return (
                      <strong key={textPartIndex} className="font-semibold">
                        {textPart.replace(/\*\*/g, '')}
                      </strong>
                    );
                  }
                  return <span key={textPartIndex}>{textPart}</span>;
                })}
              </p>
            );
          })}
        </React.Fragment>
      );
    });
  };

  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4 animate-slide-up`}
    >
      <div
        className={`max-w-[85%] md:max-w-[75%] rounded-2xl px-4 py-3 shadow-lg ${
          isUser
            ? 'bg-gradient-to-br from-green-600 to-green-700 text-white'
            : 'bg-white text-gray-800 border border-green-100'
        }`}
      >
        {/* Contenu du message */}
        <div className="prose prose-sm max-w-none">
          {formatMessage(message.content)}
        </div>

        {/* Métadonnées */}
        {message.metadata && (
          <div className="mt-2 pt-2 border-t border-opacity-20">
            {message.metadata.arabicText && (
              <div className="text-sm opacity-90" dir="rtl">
                {message.metadata.arabicText}
              </div>
            )}
            {message.metadata.reference && (
              <div className="text-xs opacity-75 mt-1">
                📚 {message.metadata.reference}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        {!isUser && (
          <div className="flex items-center space-x-2 mt-3 pt-2 border-t border-opacity-20">
            <button
              onClick={handleShare}
              className="p-1.5 hover:bg-white/20 rounded transition-colors text-xs flex items-center space-x-1"
              title="Partager"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              <span>Partager</span>
            </button>
            {onPlayAudio && (
              <button
                onClick={handlePlayAudio}
                disabled={isPlayingAudio}
                className="p-1.5 hover:bg-white/20 rounded transition-colors text-xs flex items-center space-x-1 disabled:opacity-50"
                title="Lire à voix haute"
              >
                {isPlayingAudio ? (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
                <span>Audio</span>
              </button>
            )}
            {isAuthenticated && (
              <button
                onClick={isFavorite ? handleRemoveFromFavorites : handleAddToFavorites}
                className={`p-1.5 hover:bg-white/20 rounded transition-colors text-xs flex items-center space-x-1 ${
                  isFavorite ? 'text-yellow-400' : ''
                }`}
                title={isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
              >
                <svg className="w-4 h-4" fill={isFavorite ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
                <span>Favoris</span>
              </button>
            )}
          </div>
        )}

        {/* Timestamp */}
        <div
          className={`text-xs mt-2 opacity-70 ${
            isUser ? 'text-right' : 'text-left'
          }`}
        >
          {message.timestamp.toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      </div>
    </div>
  );
};

